import { type ComponentType, type ReactNode, useCallback, useState } from 'react';
import {
  type BaseModalProps,
  ModalProviderContext,
  type ModalProviderContextValue,
} from './ModalProviderContext';

type ModalProps<T> = T & BaseModalProps;
type ModalObject<T> = {
  Modal: ComponentType<ModalProps<T>>;
  props: T;
  open: boolean;
  id: string;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<ModalObject<any>[]>([]);

  const handleOpenModal: ModalProviderContextValue['openModal'] = useCallback((Modal, props) => {
    const id =
      globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setModals(current => [...current, { Modal, open: true, id, props: (props ?? {}) as any }]);
  }, []);

  const handleCloseModal = useCallback((id: string) => {
    setModals(current =>
      current.map(modal => (modal.id === id ? { ...modal, open: false } : modal)),
    );

    setTimeout(() => {
      setModals(current => current.filter(modal => modal.id !== id));
    }, 500);
  }, []);

  const handleCloseAllModal = useCallback(() => {
    setModals(current => current.map(modal => ({ ...modal, open: false })));

    setTimeout(() => {
      setModals([]);
    }, 500);
  }, []);

  return (
    <ModalProviderContext.Provider
      value={{ openModal: handleOpenModal, onCloseAll: handleCloseAllModal, modals }}
    >
      {children}
      {modals.map(({ Modal, id, open, props }) => (
        <Modal {...props} key={id} open={open} onClose={() => handleCloseModal(id)} />
      ))}
    </ModalProviderContext.Provider>
  );
};
